import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPatch } from "../../api/client";
import {
  XI_DOMAINS,
  DOMAIN_LABELS,
  type XiDomain,
} from "@playstudio/shared";

type Selection = XiDomain | "all";

export function DomainSwitcher({
  workspaceId,
  currentDomain,
  cxoRoleTag,
  userDomains,
}: {
  workspaceId: string;
  currentDomain: XiDomain | null;
  cxoRoleTag: string;
  userDomains: (XiDomain | null)[];
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (domain: XiDomain | null) =>
      apiPatch(`/api/workspaces/${workspaceId}`, { defaultDomain: domain }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["me"] });
      void qc.invalidateQueries({ queryKey: ["roadmap"] });
      void qc.invalidateQueries({ queryKey: ["plays"] });
      setOpen(false);
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const label = currentDomain ? DOMAIN_LABELS[currentDomain] : "All my domains";

  // The user is "domain-restricted" only when their membership rows include
  // explicit (non-null) domain assignments AND no all-domains row. Today the
  // mirror always inserts a NULL row, so this is mostly future-facing — but
  // the switcher hides domains the user isn't entitled to see.
  const hasAllAccess = userDomains.includes(null);
  const allowedDomains: XiDomain[] = hasAllAccess
    ? [...XI_DOMAINS]
    : (userDomains.filter((d): d is XiDomain => d !== null));

  const selected: Selection = currentDomain ?? "all";
  const select = (s: Selection) =>
    mutation.mutate(s === "all" ? null : s);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Switch your active XI domain for this workspace"
        aria-label="Switch domain"
        className="tag"
        data-testid="domain-switcher"
        style={{
          cursor: "pointer",
          background: "transparent",
          border: "1px solid var(--border)",
          borderRadius: 999,
          padding: "1px 10px",
          font: "inherit",
          fontSize: 11,
          color: "inherit",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <span>{label}</span>
        <span style={{ opacity: 0.55 }}>·</span>
        <span style={{ opacity: 0.7 }}>{cxoRoleTag}</span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>▾</span>
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-label="XI domain"
          data-testid="domain-switcher-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            minWidth: 220,
            background: "var(--bg)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow)",
            padding: 4,
            listStyle: "none",
            margin: 0,
            zIndex: 60,
          }}
        >
          {hasAllAccess ? (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={selected === "all"}
                disabled={mutation.isPending}
                onClick={() => select("all")}
                style={menuItemStyle(selected === "all", mutation.isPending)}
                data-testid="domain-option-all"
              >
                All my domains
              </button>
            </li>
          ) : null}
          {allowedDomains.map((d) => (
            <li key={d}>
              <button
                type="button"
                role="option"
                aria-selected={selected === d}
                disabled={mutation.isPending}
                onClick={() => select(d)}
                style={menuItemStyle(selected === d, mutation.isPending)}
                data-testid={`domain-option-${d}`}
              >
                {DOMAIN_LABELS[d]}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function menuItemStyle(active: boolean, busy: boolean): React.CSSProperties {
  return {
    width: "100%",
    textAlign: "left",
    background: active ? "var(--bg-soft)" : "transparent",
    border: 0,
    padding: "6px 10px",
    borderRadius: 6,
    cursor: busy ? "wait" : "pointer",
    font: "inherit",
    fontSize: 13,
  };
}
